import { match } from 'ts-pattern'
import { z } from 'zod'
import { useSSEStream } from '../../hooks/use-sse-stream.ts'
import { buildPath } from '../../utils/build-path.ts'

// Hand-maintained mirror of the freeform upload-session SSE union defined in the v1 integration
// contract §B8 (pixee-platform). The events endpoint has no OpenAPI payload schema, so — like the
// coding-guidelines document events — these are hand-written zod discriminated on `type`.
export const FreeformStatusPhaseSchema = z.enum(['parsing', 'associating', 'scanning', 'completed', 'failed'])
export type FreeformStatusPhase = z.infer<typeof FreeformStatusPhaseSchema>

const FreeformStatusEventSchema = z.object({
  type: z.literal('status'),
  phase: FreeformStatusPhaseSchema,
  detail: z.string().optional(),
})

const FreeformFileEventSchema = z.object({
  type: z.literal('file'),
  filename: z.string(),
  parseStatus: z.string(),
})

// `lowestConfidence`/`weakestRationale` describe the association the agent was least sure of in this
// repository's group. Both are optional: the agent may not emit a confidence, and older sessions
// replayed from before the field existed will not carry one.
const FreeformRepoScanEventSchema = z.object({
  type: z.literal('repo_scan'),
  repoFullName: z.string(),
  scanId: z.string(),
  findingCount: z.number().int(),
  lowestConfidence: z.number().optional(),
  weakestRationale: z.string().optional(),
})

// Each unassociated finding carries the machine-readable outcome (`reason`) and the agent's prose
// (`rationale`) explaining it. `reason` is left as a free string rather than an enum on purpose: the
// wire vocabulary is the union of the analysis-service association taxonomy and the platform's own
// scan-creation outcomes, and either side may add a value — the UI maps known reasons and falls back
// gracefully. `findings` is optional so a platform from before the enriched event (or a session
// replayed from before it) that sends only `count` still parses.
const FreeformUnassociatedItemSchema = z.object({
  title: z.string(),
  reason: z.string(),
  rationale: z.string().optional().default(''),
})
export type FreeformUnassociatedItem = z.infer<typeof FreeformUnassociatedItemSchema>

const FreeformUnassociatedEventSchema = z.object({
  type: z.literal('unassociated'),
  count: z.number().int(),
  findings: z.array(FreeformUnassociatedItemSchema).optional(),
})

const FreeformDoneEventSchema = z.object({
  type: z.literal('done'),
})

const FreeformErrorEventSchema = z.object({
  type: z.literal('error'),
  reason: z.string(),
})

export const FreeformUploadEventSchema = z.discriminatedUnion('type', [
  FreeformStatusEventSchema,
  FreeformFileEventSchema,
  FreeformRepoScanEventSchema,
  FreeformUnassociatedEventSchema,
  FreeformDoneEventSchema,
  FreeformErrorEventSchema,
])

export type FreeformUploadEvent = z.infer<typeof FreeformUploadEventSchema>

const isTerminalEvent = (event: FreeformUploadEvent): boolean =>
  match(event.type)
    .with('done', 'error', () => true)
    .otherwise(() => false)

type UseFreeformUploadEventsOptions = {
  organizationId?: string
  sessionId: string | undefined
  onEvent?: (event: FreeformUploadEvent) => unknown
}

export const useFreeformUploadEvents = ({
  organizationId = 'default-organization',
  sessionId,
  onEvent,
}: UseFreeformUploadEventsOptions) => {
  const url =
    sessionId !== undefined
      ? buildPath('/api/v1/organizations/:organizationId/freeform-uploads/:sessionId/events', {
          organizationId,
          sessionId,
        })
      : undefined

  return useSSEStream({
    url,
    schema: FreeformUploadEventSchema,
    isFinished: isTerminalEvent,
    onEvent,
  })
}
